import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { FiChevronDown } from 'react-icons/fi';
import { IoIosCloseCircle } from 'react-icons/io';
import { MdOutlineClose } from 'react-icons/md';
import { useSelector } from 'react-redux';
import '../styles/NewTableComponent.css';
import { customStyles } from '../utils/helper';
import { searchIcon } from '../utils/svgs';
import Filters from './Filters';
import PaginationCustom from './Pagination';

const SEARCH_DEBOUNCE_MS = 500;

const getOptionTextLabel = (option) => {
    if (typeof option?.labelKey === 'string' && option.labelKey.trim()) {
        return option.labelKey;
    }
    if (typeof option?.label === 'string' && option.label.trim()) {
        return option.label;
    }
    if (typeof option?.title === 'string' && option.title.trim()) {
        return option.title;
    }
    if (typeof option?.name === 'string' && option.name.trim()) {
        return option.name;
    }
    if (typeof option?.username === 'string' && option.username.trim()) {
        return option.username;
    }
    if (option?.value != null && typeof option?.value !== 'object') {
        return String(option.value);
    }
    return '';
};

const normalizeOption = (option) => ({
    ...option,
    value: option?.value ?? option?.id ?? option?.title ?? '',
    label: getOptionTextLabel(option),
});


const NewTableComponent = ({
    btnName,
    pageName,
    showAddBtn = false,
    onAddClick,
    columns = [],
    data = [],
    currentPage,
    setCurrentPage,
    limit,
    setLimit,
    totalItems,
    totalPages,
    onSearch,
    searchValue = '',
    loading = false,
    setSearchFilters,
    searchFilters,
    paginationLoading = false,
    showPagination = true,
    showFilter = true,
    showColumnFilter = true,
    tagsOptions = [],
    onRowClick,
    paginationClassName = '',
    limitOptions = [10, 20, 50, 100],
    expandableRows = false,
    expandableRowsComponent = null,
    expandableRowsComponentProps = {},
    expandableRowExpanded,
    expandableRowDisabled,
    expandOnRowClicked = false,
    expandOnRowDoubleClicked = false,
    expandableRowsHideExpander = false,
    expandableIcon,
    onSort,
    sortServer = false,
    tableClassName = '',
    noDataComponent = null,
    fixedHeader = true,
    fixedHeaderScrollHeight,
    stickyColumns = 2,
}) => {
    const [searchTerm, setSearchTerm] = useState(searchValue || '');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchValue || '');
    const [openFilter, setOpenFilter] = useState(null);
    const [stickyHeaderState, setStickyHeaderState] = useState({
        pinned: false,
        left: 0,
        scrollLeft: 0,
        tableWidth: 0,
        viewportWidth: 0,
        clipRight: 0,
        headerHeight: 0,
    });

    const [stickyColumnState, setStickyColumnState] = useState({ firstWidth: 0, scrolled: false });

    const { userDropdownData = [] } = useSelector((state) => state.users || {});
    const previousSearchTerm = useRef(searchValue || '');
    const filterRef = useRef(null);
    const rootRef = useRef(null);
    const tableMainRef = useRef(null);

    const resolvedNoDataComponent = noDataComponent || (
        <div className='table_no_records'>There are no records to display</div>
    );

    const filterColumns = useMemo(
        () => columns.filter((column) => column?.tableColumn === true),
        [columns]
    );

    const removeFilter = useCallback((filterName) => {
        if (!setSearchFilters) return;
        setSearchFilters((previous = {}) => {
            const updated = { ...previous };
            delete updated[filterName];
            return updated;
        });
    }, [setSearchFilters]);

    const buildDisplayLabel = useCallback((column, rawValue, filterType) => {
        if (rawValue == null) return '';
        if (rawValue === 'in_progress') return 'In Progress';

        const resolvedRawValue = typeof rawValue === 'object'
            ? (rawValue.labelKey || (typeof rawValue.label === 'string' ? rawValue.label : null) || rawValue.title || rawValue.name || rawValue.value || '')
            : rawValue;

        const normalizedOptions = (column?.options || []).map(normalizeOption);
        const optionMatch = normalizedOptions.find(
            (option) =>
                String(option.value) == String(resolvedRawValue) ||
                (typeof option.label === 'string' && option.label.toLowerCase() == String(resolvedRawValue).toLowerCase())
        );

        if (optionMatch && typeof optionMatch.label === 'string' && optionMatch.label) {
            return optionMatch.label;
        }

        const isUserColumn = ['reporterFilter', 'assigneeFilter', 'issueByFilter'].includes(column?.filterName);
        if (isUserColumn) {
            const userMatch = userDropdownData.find(
                (user) =>
                    String(user.value) === String(resolvedRawValue) ||
                    String(user.label) === String(resolvedRawValue) ||
                    String(user.id) === String(resolvedRawValue)
            );
            if (userMatch) {
                const userLabel = (typeof userMatch.label === 'string' && userMatch.label) ||
                    (typeof userMatch.labelKey === 'string' && userMatch.labelKey) ||
                    userMatch.name ||
                    userMatch.username;
                if (userLabel) return userLabel;
            }
            return typeof resolvedRawValue === 'string' ? resolvedRawValue : String(resolvedRawValue ?? '');
        }

        return typeof resolvedRawValue === 'string' ? resolvedRawValue : String(resolvedRawValue ?? '');
    }, [userDropdownData]);


    useEffect(() => {
        const normalizedSearchValue = searchValue || '';
        setSearchTerm(normalizedSearchValue);
        setDebouncedSearchTerm(normalizedSearchValue);
        previousSearchTerm.current = normalizedSearchValue;
    }, [searchValue]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
        };
    }, [searchTerm]);

    useEffect(() => {
        if (onSearch && debouncedSearchTerm !== previousSearchTerm.current) {
            previousSearchTerm.current = debouncedSearchTerm;
            onSearch(debouncedSearchTerm);
        }
    }, [debouncedSearchTerm, onSearch]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target) &&
                !event.target.closest('.select-clear-button')
            ) {
                setOpenFilter(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const useInternalFixedHeader = fixedHeader && Boolean(fixedHeaderScrollHeight);
    const enableScreenStickyHeader = fixedHeader && !useInternalFixedHeader;

    const updateScreenStickyHeader = useCallback(() => {
        if (!enableScreenStickyHeader || !tableMainRef.current || typeof window === 'undefined') {
            return;
        }

        const tableMain = tableMainRef.current;
        const scrollContainer = tableMain.firstElementChild;
        const tableElement = tableMain.querySelector('.rdt_Table');
        const headerElement = tableMain.querySelector('.rdt_TableHead');

        if (!scrollContainer || !tableElement || !headerElement) return;

        const tableRect = tableMain.getBoundingClientRect();
        const headerRect = headerElement.getBoundingClientRect();
        const headerHeight = Math.ceil(headerRect.height || 0);
        const viewportWidth = Math.ceil(tableMain.clientWidth || tableRect.width || 0);
        const tableWidth = Math.ceil(tableElement.scrollWidth || tableElement.getBoundingClientRect().width || viewportWidth);
        const scrollLeft = Math.ceil(scrollContainer.scrollLeft || 0);
        const clipRight = Math.max(0, tableWidth - scrollLeft - viewportWidth);
        const shouldPin = tableRect.top <= 0 && tableRect.bottom > headerHeight + 4;

        const nextState = {
            pinned: shouldPin,
            left: Math.round(tableRect.left),
            scrollLeft,
            tableWidth,
            viewportWidth,
            clipRight,
            headerHeight,
        };

        setStickyHeaderState((previousState) => {
            const hasChanged = Object.keys(nextState).some(
                (key) => previousState[key] !== nextState[key]
            );

            return hasChanged ? nextState : previousState;
        });
    }, [enableScreenStickyHeader]);

    useEffect(() => {
        if (!enableScreenStickyHeader || typeof window === 'undefined') {
            setStickyHeaderState((previousState) =>
                previousState.pinned ? { ...previousState, pinned: false } : previousState
            );
            return undefined;
        }

        let animationFrameId = null;
        const scheduleUpdate = () => {
            if (animationFrameId) {
                window.cancelAnimationFrame(animationFrameId);
            }

            animationFrameId = window.requestAnimationFrame(updateScreenStickyHeader);
        };

        scheduleUpdate();
        window.addEventListener('resize', scheduleUpdate);
        window.addEventListener('scroll', scheduleUpdate, true);

        const scrollContainer = tableMainRef.current?.firstElementChild;
        scrollContainer?.addEventListener('scroll', scheduleUpdate, { passive: true });

        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(scheduleUpdate)
            : null;

        if (resizeObserver) {
            if (rootRef.current) resizeObserver.observe(rootRef.current);
            if (tableMainRef.current) resizeObserver.observe(tableMainRef.current);
        }

        return () => {
            if (animationFrameId) {
                window.cancelAnimationFrame(animationFrameId);
            }
            window.removeEventListener('resize', scheduleUpdate);
            window.removeEventListener('scroll', scheduleUpdate, true);
            scrollContainer?.removeEventListener('scroll', scheduleUpdate);
            resizeObserver?.disconnect();
        };
    }, [
        columns.length,
        data.length,
        enableScreenStickyHeader,
        loading,
        paginationLoading,
        showPagination,
        updateScreenStickyHeader,
    ]);

    // Pin the leading columns so the row identity stays visible while scrolling sideways.
    const stickyColumnCount = Math.min(Math.max(Number(stickyColumns) || 0, 0), 2);
    const hasExpanderCell = expandableRows && !expandableRowsHideExpander;
    const stickyColumnsEnabled = stickyColumnCount > 0 && !loading && data.length > 0;

    useEffect(() => {
        if (!stickyColumnsEnabled) {
            setStickyColumnState((previous) => (
                previous.firstWidth === 0 && !previous.scrolled
                    ? previous
                    : { firstWidth: 0, scrolled: false }
            ));
            return undefined;
        }

        const tableMain = tableMainRef.current;
        if (!tableMain) return undefined;

        const scroller = tableMain.firstElementChild;
        const headRow = tableMain.querySelector('.rdt_TableHeadRow');

        // Measure the gap between the first two cells rather than cell 1's width:
        // the head and body cells must line up, and offsetLeft already accounts for
        // padding, borders and the expander cell. Body row wins because that is what
        // the pinned heading has to sit above; head row is the fallback for empty tables.
        const measure = () => {
            const referenceRow = tableMain.querySelector('.rdt_TableBody .rdt_TableRow') || headRow;
            const cells = referenceRow ? Array.from(referenceRow.children) : [];
            const startIndex = hasExpanderCell ? 1 : 0;
            const firstCell = cells[startIndex];
            const secondCell = cells[startIndex + 1];

            let firstWidth = 0;
            if (firstCell && secondCell) {
                firstWidth = Math.max(secondCell.offsetLeft - firstCell.offsetLeft, 0);
            } else if (firstCell) {
                firstWidth = firstCell.getBoundingClientRect().width;
            }

            const scrolled = scroller ? scroller.scrollLeft > 0 : false;

            setStickyColumnState((previous) => (
                Math.abs(previous.firstWidth - firstWidth) < 0.5 && previous.scrolled === scrolled
                    ? previous
                    : { firstWidth, scrolled }
            ));
        };

        measure();

        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (observer && headRow) observer.observe(headRow);
        if (observer && scroller) observer.observe(scroller);
        if (scroller) scroller.addEventListener('scroll', measure, { passive: true });
        window.addEventListener('resize', measure);

        return () => {
            if (observer) observer.disconnect();
            if (scroller) scroller.removeEventListener('scroll', measure);
            window.removeEventListener('resize', measure);
        };
    }, [stickyColumnsEnabled, hasExpanderCell, columns, data.length]);

    const wrapperClassName = [
        'wrapper_data_table',
        enableScreenStickyHeader ? 'table_has_screen_sticky_header' : '',
        stickyHeaderState.pinned ? 'table_header_pinned' : '',
        stickyColumnsEnabled ? 'table_has_sticky_columns' : '',
        stickyColumnsEnabled ? `table_sticky_cols_${stickyColumnCount}` : '',
        stickyColumnsEnabled && hasExpanderCell ? 'table_sticky_has_expander' : '',
        stickyColumnsEnabled && stickyColumnState.scrolled ? 'table_sticky_cols_scrolled' : '',
        tableClassName,
        !loading && data.length === 0 ? 'table_is_empty' : '',
    ].filter(Boolean).join(' ');
    const wrapperStyle = (enableScreenStickyHeader || stickyColumnsEnabled) ? {
        ...(enableScreenStickyHeader ? {
            '--table-sticky-left': `${stickyHeaderState.left}px`,
            '--table-sticky-scroll-left': `${stickyHeaderState.scrollLeft}px`,
            '--table-sticky-table-width': `${stickyHeaderState.tableWidth}px`,
            '--table-sticky-viewport-width': `${stickyHeaderState.viewportWidth}px`,
            '--table-sticky-clip-right': `${stickyHeaderState.clipRight}px`,
            '--table-sticky-header-height': `${stickyHeaderState.headerHeight}px`,
        } : {}),
        ...(stickyColumnsEnabled ? {
            '--table-sticky-col-first-width': `${stickyColumnState.firstWidth}px`,
        } : {}),
    } : undefined;

    return (
        <div ref={rootRef}>
            <div className={wrapperClassName} style={wrapperStyle}>
                {showFilter && (
                    <div className={`table_controls_wrapper ${openFilter ? 'table_filters_open' : ''}`}>
                        <div className='table_header_search'>
                            <div>
                                {searchIcon}
                                <input
                                    type='text'
                                    placeholder='Search'
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                />
                                {searchTerm.length > 0 && (
                                    <span onClick={() => setSearchTerm('')}>
                                        <MdOutlineClose />
                                    </span>
                                )}
                            </div>
                            {showAddBtn && (
                                <button className='add_btn' style={{ minWidth: pageName ? '215px' : '120px' }} onClick={onAddClick}>
                                    + {btnName}
                                </button>
                            )}
                        </div>

                        {showColumnFilter && filterColumns.length > 0 && (
                            <div className='table_column'>
                                {filterColumns.map((column, columnIndex) => {
                                    const filterName = column.filterName;
                                    const filterObj = searchFilters?.[filterName];
                                    const isActive = filterObj?.filter_type && filterObj?.value;
                                    const shouldAlignFilterRight = columnIndex >= filterColumns.length - 2;
                                    const columnFilterLabel = column?.filterLabel || column?.name || filterName;
                                    const filterValueLabel = filterObj?.filter_type === 'date_range' && typeof filterObj?.value === 'object'
                                        ? `${filterObj?.value?.startDate || ''} to ${filterObj?.value?.endDate || ''}`
                                        : filterObj?.filter_type === 'one_of'
                                            ? Array.isArray(filterObj.value)
                                                ? filterObj.value.map((value) => buildDisplayLabel(column, value, filterObj?.filter_type)).join(', ')
                                                : buildDisplayLabel(column, filterObj.value, filterObj?.filter_type)
                                            : buildDisplayLabel(column, filterObj?.value, filterObj?.filter_type);

                                    const isOpen = openFilter === filterName;

                                    return (
                                        <div
                                            key={filterName || column.name}
                                            className={`filter_dropdown_wrapper ${isOpen ? 'open' : ''}`}
                                            style={{ position: 'relative', zIndex: isOpen ? 2000 : 10 }}
                                        >
                                            {isActive ? (
                                                <>
                                                    <div
                                                        className='filter_chip'
                                                        style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                                                        onClick={() => setOpenFilter(filterName)}
                                                    >
                                                        {columnFilterLabel}: {filterObj.filter_type.replace(/_/g, ' ')} "{filterValueLabel}"
                                                        <IoIosCloseCircle
                                                            style={{ cursor: 'pointer', marginLeft: 6, fontSize: '18px', width: '18px', height: '18px', flexShrink: 0, color: '#4014B8', fill: 'currentColor' }}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                removeFilter(filterName);
                                                            }}
                                                        />
                                                    </div>
                                                    {openFilter === filterName && (
                                                        column.filterComponent ? (
                                                            <column.filterComponent
                                                                options={column.options}
                                                                isMulti={column.isMulti}
                                                                filterName={filterName}
                                                                searchFilters={searchFilters}
                                                                setSearchFilters={setSearchFilters}
                                                                closeFilter={() => setOpenFilter(null)}
                                                                columnName={column.name}
                                                                alignRight={shouldAlignFilterRight}
                                                                setWrapperRef={(element) => {
                                                                    filterRef.current = element;
                                                                }}
                                                            />
                                                        ) : (
                                                            <Filters
                                                                closeFilter={() => setOpenFilter(null)}
                                                                searchFilters={searchFilters}
                                                                setSearchFilters={setSearchFilters}
                                                                filterName={filterName}
                                                                type={column.type}
                                                                operatorOptions={column.operatorOptions}
                                                                component={column.component || null}
                                                                {...(filterName === 'tagsFilter' ? { tagsOptions } : {})}
                                                                options={column.options}
                                                                alignRight={shouldAlignFilterRight}
                                                                setWrapperRef={(element) => {
                                                                    filterRef.current = element;
                                                                }}
                                                                removeFilter={() => removeFilter(filterName)}
                                                            />
                                                        )
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <span onClick={() => setOpenFilter(filterName)}>
                                                        {columnFilterLabel}
                                                        <FiChevronDown />
                                                    </span>
                                                    {openFilter === filterName && (
                                                        column.filterComponent ? (
                                                            <column.filterComponent
                                                                options={column.options}
                                                                isMulti={column.isMulti}
                                                                filterName={filterName}
                                                                searchFilters={searchFilters}
                                                                setSearchFilters={setSearchFilters}
                                                                closeFilter={() => setOpenFilter(null)}
                                                                columnName={column.name}
                                                                alignRight={shouldAlignFilterRight}
                                                                setWrapperRef={(element) => {
                                                                    filterRef.current = element;
                                                                }}
                                                            />
                                                        ) : (
                                                            <Filters
                                                                closeFilter={() => setOpenFilter(null)}
                                                                searchFilters={searchFilters}
                                                                setSearchFilters={setSearchFilters}
                                                                filterName={filterName}
                                                                type={column.type}
                                                                operatorOptions={column.operatorOptions}
                                                                component={column.component || null}
                                                                {...(filterName === 'tagsFilter' ? { tagsOptions } : {})}
                                                                options={column.options}
                                                                alignRight={shouldAlignFilterRight}
                                                                setWrapperRef={(element) => {
                                                                    filterRef.current = element;
                                                                }}
                                                                removeFilter={() => removeFilter(filterName)}
                                                            />
                                                        )
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {loading ? (
                    <div className='loading_spinner'>
                        <Spinner animation='grow' />
                    </div>
                ) : (
                    <div className='data_table_main' ref={tableMainRef}>
                        <DataTable
                            columns={columns}
                            data={data}
                            fixedHeader={useInternalFixedHeader}
                            fixedHeaderScrollHeight={fixedHeaderScrollHeight}
                            pointerOnHover
                            striped={false}
                            dense={false}
                            customStyles={customStyles}
                            onRowClicked={onRowClick}
                            onSort={onSort}
                            sortServer={sortServer}
                            expandableRows={expandableRows}
                            expandableRowsComponent={expandableRowsComponent}
                            expandableRowsComponentProps={expandableRowsComponentProps}
                            expandableRowExpanded={expandableRowExpanded}
                            expandableRowDisabled={expandableRowDisabled}
                            expandOnRowClicked={expandOnRowClicked}
                            expandOnRowDoubleClicked={expandOnRowDoubleClicked}
                            expandableRowsHideExpander={expandableRowsHideExpander}
                            expandableIcon={expandableIcon}
                            noDataComponent={resolvedNoDataComponent}
                        />
                    </div>
                )}
            </div>

            {!paginationLoading && showPagination && (
                <PaginationCustom
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    limit={limit}
                    setLimit={setLimit}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    className={paginationClassName}
                    limitOptions={limitOptions}
                />
            )}
        </div>
    );
};

export default NewTableComponent;
